export default function initAssortimentMenu(): void {
  Alpine.data("assortimentMenu", () => ({
    lvl1Active: null as string | null,
    lvl2Active: null as string | null,

    init() {
      const getLvl1Items = (): HTMLElement[] =>
        Array.from(this.$refs.popover.querySelectorAll('[data-level="1"]'));
      const getLvl2Items = (): HTMLElement[] =>
        Array.from(this.$refs.popover.querySelectorAll('[data-level="2"]'));
      const getLvl1ChildItems = (): HTMLElement[] =>
        Array.from(this.$refs.popover.querySelectorAll("[data-parent-node]"));
      const getLvl2ChildItems = (): HTMLElement[] =>
        Array.from(this.$refs.popover.querySelectorAll("[data-parent-2-node]"));

      // Manage page scroll when popover opens/closes
      this.$refs.popover.addEventListener("toggle", (e: ToggleEvent) => {
        if (e.newState === "open") {
          document.documentElement.classList.add("tw:overflow-hidden");
          // Set first lvl1 and first lvl2 item active by default on desktop
          if (!window.matchMedia("(max-width: 1024px)").matches) {
            const lvl1Items = Array.from(this.$refs.popover.querySelectorAll('[data-level="1"]'));
            if (lvl1Items.length > 0) {
              this.lvl1Active = lvl1Items[0].id;
              // Find first lvl2 item under this lvl1 (corrected selector)
              const lvl2Container = this.$refs.popover.querySelector(`[data-parent-node="${lvl1Items[0].id}"]`);
              const lvl2Items = lvl2Container ? Array.from(lvl2Container.querySelectorAll('[data-level="2"]')) : [];
              if (lvl2Items.length > 0) {
                this.lvl2Active = lvl2Items[0].id;
              } else {
                this.lvl2Active = null;
              }
            } else {
              this.lvl1Active = null;
              this.lvl2Active = null;
            }
          }
        } else {
          document.documentElement.classList.remove("tw:overflow-hidden");
          this.lvl1Active = null;
          this.lvl2Active = null;
        }
      });

      // Event delegation for clicks (robust even if content mounts later)
      this.$refs.popover.addEventListener(
        "click",
        (e: MouseEvent) => {
          const target = e.target as Element | null;
          if (!target) return;
          const anchor: HTMLElement | null = target.closest("a[data-level]");
          if (!anchor) return;
          const { level } = anchor.dataset;

          if (window.matchMedia("(max-width: 1024px)").matches) {
            if (level === "1") {
              const hasChildren =
                anchor.dataset.hasChildren === "true" ||
                getLvl1ChildItems().some(
                  (child) => child.dataset.parentNode === anchor.id,
                );
              if (hasChildren) {
                e.preventDefault();
                e.stopPropagation();
                this.lvl1Active = anchor.id;
                this.lvl2Active = null;
                // Immediate show/hide for lvl2 containers
                getLvl1ChildItems().forEach((item) => {
                  if (item.dataset.parentNode === anchor.id)
                    item.classList.remove("tw:hidden");
                  else item.classList.add("tw:hidden");
                });
                this.$refs.lvl1Container.classList.add("tw:hidden");
              }
            } else if (level === "2") {
              const hasChildren =
                anchor.dataset.hasChildren === "true" ||
                getLvl2ChildItems().some(
                  (child) => child.dataset.parent2Node === anchor.id,
                );
              if (hasChildren) {
                e.preventDefault();
                e.stopPropagation();
                this.lvl2Active = anchor.id;
                // Immediate show/hide for lvl3 containers + hide lvl2
                getLvl2ChildItems().forEach((item) => {
                  if (item.dataset.parent2Node === anchor.id)
                    item.classList.remove("tw:hidden");
                  else item.classList.add("tw:hidden");
                });
                getLvl1ChildItems().forEach((item) =>
                  item.classList.add("tw:hidden"),
                );
                this.$refs.lvl1Container.classList.add("tw:hidden");
              }
            }
          }
        },
        { capture: true },
      );

      // Desktop hover behavior via delegation (mouseenter doesn't bubble; use mouseover)
      this.$refs.popover.addEventListener("mouseover", (e: MouseEvent) => {
        if (window.matchMedia("(max-width: 1024px)").matches) return;
        const target = e.target as Element | null;
        if (!target) return;
        const lvl1Anchor = target.closest('a[data-level="1"]');
        const lvl2Anchor = target.closest('a[data-level="2"]');
        if (lvl1Anchor) {
          this.lvl1Active = lvl1Anchor.id;
          // Set first lvl2 item under new lvl1 (corrected selector)
          const lvl2Container = this.$refs.popover.querySelector(`[data-parent-node="${lvl1Anchor.id}"]`);
          const lvl2Items = lvl2Container ? Array.from(lvl2Container.querySelectorAll('[data-level="2"]')) : [];
          if (lvl2Items.length > 0) {
            this.lvl2Active = lvl2Items[0].id;
          } else {
            this.lvl2Active = null;
          }
        } else if (lvl2Anchor) {
          this.lvl2Active = lvl2Anchor.id;
        }
      });

      this.$watch("lvl1Active", (newValue) => {
        if (newValue === null) {
          this.lvl2Active = null;
          this.$refs.lvl1Container.classList.remove("tw:hidden");
          getLvl1Items().forEach((item: HTMLElement) => {
            item.classList.remove("active");
          });
          getLvl1ChildItems().forEach((item: HTMLElement) => {
            item.classList.add("tw:hidden");
          });
        } else if (window.matchMedia("(max-width: 1024px)").matches) {
          this.$refs.lvl1Container.classList.add("tw:hidden");
        } else {
          getLvl1Items().forEach((item: HTMLElement) => {
            if (item.id === newValue) {
              item.classList.add("active");
            } else {
              item.classList.remove("active");
            }
          });
          getLvl1ChildItems().forEach((item: HTMLElement) => {
            if (item.dataset.parentNode === newValue) {
              item.classList.remove("tw:hidden");
            } else {
              item.classList.add("tw:hidden");
            }
          });
        }
      });

      this.$watch("lvl2Active", (newValue) => {
        if (newValue === null) {
          this.lvl2Active = null;
        }
        getLvl2Items().forEach((item: HTMLElement) => {
          if (item.id === newValue) {
            item.classList.add("active");
          } else {
            item.classList.remove("active");
          }
        });
        getLvl2ChildItems().forEach((item: HTMLElement) => {
          if (item.dataset["parent-2Node"] === newValue) {
            item.classList.remove("tw:hidden");
          } else {
            item.classList.add("tw:hidden");
          }
        });

        // On mobile, ensure only one level is visible at a time
        if (window.matchMedia("(max-width: 1024px)").matches) {
          // Hide all lvl2 containers when a lvl3 is active
          if (newValue !== null) {
            getLvl1ChildItems().forEach((item: HTMLElement) => {
              item.classList.add("tw:hidden");
            });
            // Also ensure lvl1 is hidden
            this.$refs.lvl1Container.classList.add("tw:hidden");
          }
        }
      });

      // Direct binding fallback: ensure anchors have click handlers even if delegation doesn't catch
      const bindAnchors = () => {
        getLvl1Items().forEach((item) => {
          item.addEventListener(
            "click",
            (e) => {
              if (!window.matchMedia("(max-width: 1024px)").matches) return;
              const hasChildren =
                item.dataset.hasChildren === "true" ||
                getLvl1ChildItems().some(
                  (child) => child.dataset.parentNode === item.id,
                );
              if (hasChildren) {
                e.preventDefault();
                e.stopPropagation();
                this.lvl1Active = item.id;
                this.lvl2Active = null;
              }
            },
            { capture: true },
          );
        });
        getLvl2Items().forEach((item) => {
          item.addEventListener(
            "click",
            (e) => {
              if (!window.matchMedia("(max-width: 1024px)").matches) return;
              const hasChildren =
                item.dataset.hasChildren === "true" ||
                getLvl2ChildItems().some(
                  (child) => child.dataset.parent2Node === item.id,
                );
              if (hasChildren) {
                e.preventDefault();
                e.stopPropagation();
                this.lvl2Active = item.id;
              }
            },
            { capture: true },
          );
        });
      };

      // Bind immediately and on popover open
      bindAnchors();
      this.$refs.popover.addEventListener("toggle", (e: ToggleEvent) => {
        if (e.newState === "open") {
          bindAnchors();
        }
      });
    },
    back() {
      if (this.lvl1Active && this.lvl2Active === null) {
        this.lvl1Active = null;
      } else {
        this.lvl2Active = null;
        Array.from(
          this.$refs.popover.querySelectorAll("[data-parent-node]"),
        ).forEach((item) => {
          if (item instanceof HTMLElement) {
            if (item.dataset.parentNode === this.lvl1Active) {
              item.classList.remove("tw:hidden");
            } else {
              item.classList.add("tw:hidden");
            }
          }
        });
      }
    },
  }));
}
